import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import {
  addSalaryAction,
  editSalaryAction,
  fetchSalariesAction,
  deleteSalaryAction,
} from "../redux/actions/empSalaryAction";
import { EmployeeSalary as SalaryType } from "../types/EmployeeSalary";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as Feather from "react-feather";
import Papa from "papaparse";
import Main from "../components/custom/Main";
import PageContainer from "../components/custom/PageContainer";
import Modal from "../components/Modal";
import CollapseCard from "../components/custom/CollapseCard";
import { useToast } from "../components/toast/ToastProvider";
import { StoreState } from "../redux/reducers";
import EmployeeSalaryForm from "../components/EmployeeSalaryForm";
import EmployeeSalaryList from "../components/EmployeeSalaryList";
import { addSalaryLogAction } from "../redux/actions/salaryMasterLogAction";
import { SalaryMasterLog } from "../types/SalaryMasterLog";
import { fetchSalariesMasterActionBYID } from "../redux/actions/salaryMasterAction";
import EmployeeSalaryFilter from "../components/EmployeeSalaryFilter";

const EmployeeSalaryPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();

  const salaryList = useSelector((state: StoreState) => state.employeeSalary);
  const [selectedSalary, setSelectedSalary] = useState<SalaryType | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const salaryMaster = useSelector(
    (state: StoreState) => state.salaryMaster.selectedByEmployeeId,
  );

  useEffect(() => {
    setLoading(true);
    dispatch(fetchSalariesAction()).finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (selectedSalary?.employeeId) {
      dispatch(fetchSalariesMasterActionBYID(selectedSalary.employeeId));
    }
  }, [dispatch, selectedSalary?.employeeId]);

  const handleEdit = (salary: SalaryType) => {
    setSelectedSalary(salary);
    setShowModal(true);
  };

  const addOrUpdateHandler = async (data: SalaryType) => {
    setLoading(true);
    try {
      const isduplicates = salaryList.some(
        (sal) => sal.employeeId === data.employeeId && sal.month === data.month,
      );
      if (!selectedSalary && isduplicates) {
        toast("salary month already exist");
        setLoading(false);
        return;
      }

      const salaryLogData: SalaryMasterLog = {
        id: data.id,
        employeeId: data.employeeId,
        basicPay: data.bp,
        hra: data.hra,
        tax: data.tax,
        pf: data.pf,
        ta: data.ta,
        sa: data.sa,
        createTimestamp: new Date().toISOString(),
      };
      if (selectedSalary) {
        await dispatch(editSalaryAction(data.id, data));
        toast("Salary updated successfully");
      } else {
        await dispatch(addSalaryAction(data));
        toast("Salary created successfully");
      }

      await dispatch(addSalaryLogAction(salaryLogData));
      await dispatch(fetchSalariesAction());
      setSelectedSalary(null);
      setShowModal(false);
    } catch (error) {
      toast("Error in Save Salary");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure?")) {
      setLoading(true);
      await dispatch(deleteSalaryAction(id));
      toast("Salary deleted successfully");
      await dispatch(fetchSalariesAction());
      setLoading(false);
    }
  };

  // filters logic
  const [filters, setFilters] = useState({
    year: "",
    month: "",
    employeeId: "",
  });
  const [filteredEmpSal, setFilteredEmpSal] = useState<SalaryType[]>([]);

  useEffect(() => {
    setFilteredEmpSal(salaryList);
  }, [salaryList]);

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);

    const filtered = salaryList.filter((log) => {
      const matchesYear =
        !newFilters.year || String(log.year) === String(newFilters.year);

      const matchesMonth =
        !newFilters.month || log.month === String(newFilters.month);

      const matchesEmployee =
        !newFilters.employeeId || log.employeeId === newFilters.employeeId;
      return matchesYear && matchesMonth && matchesEmployee;
    });
    setFilteredEmpSal(filtered);
  };

  const handleReset = () => {
    setFilters({ year: "", month: "", employeeId: "" });
    setFilteredEmpSal(salaryList);
  };

  const downloadSalaryPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const title = "Employee Salary Details";
    const pageWidth = doc.internal.pageSize.getWidth();
    const titleWidth = doc.getTextWidth(title);

    doc.setFontSize(14);
    doc.text(title, (pageWidth - titleWidth) / 2, 10);

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");

    const filteredEmployees = filteredEmpSal.filter(
      (emp) => emp.employeeId !== null,
    );

    const headers = [
      "S.No",
      "Employee ID",
      "Basic Pay",
      "HRA",
      "Tax",
      "PF",
      "TA",
      "SA",
      "LOP",
      "Net Amount",
    ];

    const rows = filteredEmployees.map((emp, i) => [
      i + 1,
      emp.employeeId || "-",
      emp.bp || "-",
      emp.hra || "-",
      emp.tax || "-",
      emp.pf || "-",
      emp.ta || "-",
      emp.sa || "-",
      emp.lop || "-",
      emp.netAmount || "-",
    ]);

    autoTable(doc, {
      head: [headers],
      body: rows,
      theme: "grid",
      startY: 25,
      tableWidth: "auto",
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: "linebreak",
        textColor: [0, 0, 0],
        lineColor: 0,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 7,
        lineColor: 0,
        lineWidth: 0.1,
      },
      didDrawPage: () => {
        const pageNumber = doc.getNumberOfPages();
        const pageHeight = doc.internal.pageSize.getHeight();

        doc.setFontSize(8);
        doc.text(`Page ${pageNumber}`, pageWidth - 40, pageHeight - 10);
      },
    });

    if (doc.getNumberOfPages() > 1) {
      doc.setPage(doc.getNumberOfPages());
    }

    const pageHeight = doc.internal.pageSize.getHeight();

    if ((doc as any).lastAutoTable.finalY + 20 > pageHeight - 20) {
      doc.addPage();
    }

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");

    doc.save("employee_salary.pdf");
  };

  const downloadSalaryCSV = () => {
    const filteredEmployees = filteredEmpSal.filter(
      (emp) => emp.employeeId !== null,
    );

    // Define the fixed salary columns you want to export
    const salaryColumns = [
      "employeeId",
      "bp",
      "hra",
      "tax",
      "pf",
      "ta",
      "sa",
      "lop",
      "netAmount",
    ];

    // Build the CSV data array
    const csvData = filteredEmployees.map((emp, i) => ({
      "S.No": i + 1,
      "Employee ID": emp.employeeId || "-",
      "Basic Pay": emp.bp || "-",
      HRA: emp.hra || "-",
      Tax: emp.tax || "-",
      PF: emp.pf || "-",
      TA: emp.ta || "-",
      SA: emp.sa || "-",
      LOP: emp.lop || "-",
      "Net Amount": emp.netAmount || "-",
    }));

    // Convert data to CSV string
    const csv = Papa.unparse(csvData);

    // Create a Blob and trigger download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "employee_salary.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Main>
      <PageContainer
        title="Employee Salary"
        onButtonClick={() => setShowModal(true)}
        buttonText={loading ? "Loading..." : "ADD"}
      >
        <Modal
          visible={showModal}
          size="xl"
          onClose={() => {
            setShowModal(false);
            setSelectedSalary(null);
          }}
          headerText={selectedSalary ? "Update Salary" : "Add Salary"}
        >
          <EmployeeSalaryForm
            salaryIdToEdit={selectedSalary?.id}
            onSave={addOrUpdateHandler}
            initialData={
              selectedSalary
                ? {
                    ...selectedSalary,
                    ...(salaryMaster && {
                      gross: salaryMaster.gross,
                      basicPay: salaryMaster.basicPay,
                      hra: salaryMaster.hra,
                      tax: salaryMaster.tax,
                      pf: salaryMaster.pf,
                      ta: salaryMaster.ta,
                      sa: salaryMaster.sa,
                    }),
                    id: selectedSalary.id ?? 0,
                  }
                : null
            }
            onEmployeeChange={(employeeId: String) => {
              dispatch(fetchSalariesMasterActionBYID(employeeId));
            }}
            salaryMaster={salaryMaster}
          />
        </Modal>

        <CollapseCard title="Employee Salary Filter">
          <EmployeeSalaryFilter
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
          />
        </CollapseCard>

        <CollapseCard title="Salary List">
          <EmployeeSalaryList
            salaryList={filteredEmpSal}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CollapseCard>
        <>
          {salaryList.length > 0 && (
            <div className="col-12 m-3 d-flex justify-content-end">
              <button
                className="btn btn-success mx-2"
                onClick={downloadSalaryPDF}
                style={{ fontSize: "14px" }}
              >
                <Feather.Download style={{ width: "20px" }} /> PDF
              </button>
              <button
                className="btn btn-success mx-2"
                onClick={downloadSalaryCSV}
                style={{ fontSize: "14px" }}
              >
                <Feather.Download style={{ width: "20px" }} /> CSV
              </button>
            </div>
          )}
        </>
      </PageContainer>
    </Main>
  );
};

export default EmployeeSalaryPage;
